const ACCOUNTING_VIEW_TYPES = new Set(["holding", "workspace"]);

export function resolveSingleAssetViewState({
  activeBinding,
  directAccountingView,
  directMarketView,
  retainedWorkspace,
  turnResult,
}) {
  const bindingType = directAccountingView?.target?.type ?? activeBinding?.view_type ?? null;
  // 绑定还没物化（create_on_write，一笔都没写过）时，它只是个占位，不是核算目标。
  // 仍按"核算态"算的话会把行情态一并挡掉，右侧只剩空白——从概览「新增持仓」进来、
  // 把录入弹窗关掉就是这个下场（2026-09-04 用户提出）。
  const unmaterialisedBinding = !directAccountingView
    && String(activeBinding?.write_mode || "") === "create_on_write";
  const accountingBindingActive = !unmaterialisedBinding && Boolean(
    directAccountingView || ACCOUNTING_VIEW_TYPES.has(bindingType),
  );
  // 绑定视图是权威来源，但它可能还没取回来（本轮没有 reload_binding 时就不会刷新）。
  // 那种时候旧代码直接落到 null，右侧被渲染成"这个目标没有核算结果"——一句澄清式
  // 回复就能把已经算好的结果整块擦掉。用本轮/上一轮的结果兜底，但必须是同一只标的，
  // 免得拿别的标的的旧结果冒充当前绑定。
  const boundAssetCode = String(
    directAccountingView?.target?.asset_code ?? activeBinding?.asset_code ?? "",
  ).toUpperCase();
  // 必须是「明确同一只标的」才兜底。绑定没带标的代码时宁可不显示，也不能拿
  // 会话里的旧结果冒充权威目标——那是绑定优先这条规则本来要挡住的东西。
  const sameBoundAsset = (candidate) => Boolean(
    candidate
    && boundAssetCode
    && String(candidate.asset_code || "").toUpperCase() === boundAssetCode,
  );
  const boundFallbackResult = [
    turnResult?.calculation_result,
    retainedWorkspace?.calculationResult,
  ].find(sameBoundAsset) ?? null;
  // `??` 分不清两种 null，而它们要的行为相反：
  //   ① 绑定视图还没取回来      → 不知道 → 该兜底（上面那段注释说的就是这个）
  //   ② 绑定视图取回来了、就是空 → 已知没有 → **不许兜底**
  // 都走 ?? 的话，删光交易之后右侧会继续显示删除前的旧结果——助手说"已删除"、
  // 后端 tx=0 且 revision 已推进，右侧还挂着 1,000 股（AGENT-040-c 实测）。
  // 区分依据就在同一个对象里：facts.transactions 已经映射成 directAccountingView.rows。
  // 必须是「拿到了 rows 这个数组、而且它是空的」。缺 rows 键只说明这份视图没带
  // 事实（例如只给了 calculation 的旧形状），那仍属于"不知道"，不能当成"确认没有"。
  // 但「没有流水」不等于「没有事实」：当前持仓基线（期初持仓）按设计就不产生交易行。
  // 漏掉这个判断的话，纯基线持仓会被当成"删空了"而回落行情态——右侧不走基线口径
  // （opening_position 不显示），头部按钮变成关注类，「加入持有标的」压根不出现，
  // 基线持仓永远加不进概览。2026-08-31 由 single-asset-current-holding.spec.js 实测暴露。
  const boundFactsAreKnownEmpty = Boolean(
    directAccountingView
    && Array.isArray(directAccountingView.rows)
    && directAccountingView.rows.length === 0
    && !directAccountingView.opening_position,
  );
  // 同一个道理：视图取回来了、核算明确失败，也是「已知没有」。事实已经保存却拿旧结果兜底，
  // 就是把旧收益标成新事实的结果（transaction-dialog-ledger-edit AC-011）。
  const boundCalculationKnownFailed = Boolean(
    directAccountingView
    && Array.isArray(directAccountingView.rows)
    && directAccountingView.calculation_status === "failed",
  );
  const calculationResult = accountingBindingActive
    ? (
      boundFactsAreKnownEmpty || boundCalculationKnownFailed
        ? null
        : directAccountingView?.calculation_result ?? boundFallbackResult
    )
    : turnResult?.calculation_result ?? retainedWorkspace?.calculationResult ?? null;
  // 事实确认为空时，这个目标已经没有可展示的核算了，右侧回落到行情态
  // （产品口径 2026-08-24：删空之后右侧就变成行情态）。
  const marketView = accountingBindingActive && !boundFactsAreKnownEmpty
    ? null
    : directMarketView ?? turnResult?.market_view ?? retainedWorkspace?.marketView ?? null;
  return {
    calculationResult,
    marketView,
    displayResult: marketView ?? calculationResult,
    activeViewType: marketView ? "market" : bindingType ?? (calculationResult ? "workspace" : null),
    accountingResultReady: Boolean(calculationResult),
  };
}

export function shouldAdoptRouteBindingView(bindingView, bindingId) {
  // 路由带来了绑定视图，照单全收。
  if (bindingView) return true;
  // 没带 ≠ 当前没有绑定。每轮对话结束后 onSessionRouteChange 会 navigate 更新
  // URL 且不带 state，`location.state` 随之消失，`initialBindingView` 变成
  // undefined。旧代码据此把 activeBinding 置空，于是下一轮不再向后端送 binding，
  // 后端只能回落到恒空的旧草稿——助手对着右侧显示 1200 股的页面回答"当前这一页
  // 还没有已提交的交易，需要我帮你重新录入吗"，右侧同时被清空。
  // URL 里 `binding=` 一直都在，按 id 拉取的那个 effect 会把它恢复；这里不能清。
  // 见 2026-08-20 走查：硬刷新后同一句话就答对，几轮之后又答错。
  return !bindingId;
}

export function bindingViewRecoveryMessage(view) {
  if (!view?.recovery || view.market_view || view.calculation) return "";
  return view.recovery.message || "当前页面目标暂时无法恢复。";
}
